import { supabaseClient } from 'supabase'
import type { VenueAliasRow } from 'pages/Desk/venueNameKey'

/**
 * Every venue alias, for the desk's venue matcher (spec-editor-venue-matching
 * §A, 33.4 D-08). Admins read `venue_aliases` under RLS (0039, D-12); the
 * desk keys them with venueNameKey at load, never stored (D-11).
 *
 * One read, no paging: the table holds a handful of hand-added names per
 * venue and stays far below PostgREST's 1,000-row cap on a select. If it
 * ever nears that, move to a count or an RPC rather than paging blindly.
 *
 * Throws on error, like queryCustomVenueHistory; useQueryVenueAliases makes
 * the lookup never-throw for the desk (P-12).
 */

/**
 * "No such table" (P-13, verified on local stacks 2026-11-04): PostgREST 13+
 * answers PGRST205 ("Could not find the table ... in the schema cache"; seen
 * on PostgREST 14.1); the PostgREST 11.2 of this project's local stack answers
 * Postgres' 42P01 ("relation does not exist"). Both mean a database without
 * 0039 (prod until its held apply), whichever PostgREST it runs.
 */
const TABLE_NOT_FOUND = new Set(['PGRST205', '42P01'])

export const queryVenueAliases = async (): Promise<VenueAliasRow[]> => {
  const { data, error } = await supabaseClient
    .from('venue_aliases')
    .select('alias, venue_id')
    .order('venue_id')
    .order('alias')

  if (error) {
    // A database without the table is "no aliases": the desk matches on
    // venue names only, and no note shows. Every other error still throws.
    if (TABLE_NOT_FOUND.has(error.code)) {
      return []
    }
    throw error
  }
  return (data ?? []) as VenueAliasRow[]
}
